import { useSignal } from "@preact/signals-react";
import { toast } from "react-toastify";
import CurrencyInput from "../../../../molecules/CurrencyInput";
import SelectTextField from "../../../../molecules/SelectTextField";
import { FeeItem, FeesData } from "../../types/operations";

interface Props {
    fees: FeesData;
    onAdd: (fee: FeeItem) => void;
    onRemove: (index: number) => void;
    onUpdate: (index: number, fee: FeeItem) => void;
    disabled?: boolean;
}

const FeesManager: React.FC<Props> = ({ fees, onAdd, onRemove, onUpdate, disabled = false }) => {
    const newFee = useSignal<{ name: string; rate: number; interest_period: string }>({
        name: '',
        rate: 0,
        interest_period: ''
    });
    const editingIndex = useSignal<number | null>(null);
    const editingFee = useSignal<FeeItem>({ name: '', rate: 0, amount: 0, form: '', interest_period: '' });

    const handleAdd = () => {
        if (!newFee.value.name) {
            toast.error("Informe o nome da taxa");
            return;
        }
        if (newFee.value.rate < 0) {
            toast.error("Informe uma taxa válida");
            return;
        }

        onAdd({
            name: newFee.value.name,
            rate: newFee.value.rate,
            amount: 0,
            form: '',
            interest_period: newFee.value.interest_period
        });

        newFee.value = {
            name: '',
            rate: 0,
            interest_period: ''
        };
    };

    const handleEdit = (index: number) => {
        editingIndex.value = index;
        editingFee.value = { ...fees.items[index] };
    };

    const handleSave = (index: number) => {
        if (!editingFee.value.name) {
            toast.error("Informe o nome da taxa");
            return;
        }
        if (editingFee.value.rate < 0) {
            toast.error("Informe uma taxa válida");
            return;
        }
        onUpdate(index, editingFee.value);
        editingIndex.value = null;
    };

    const handleCancel = () => {
        editingIndex.value = null;
    };

    return (
        <div>
            <h3 className="text-md font-semibold mt-4 mb-3 text-gray-700 dark:text-gray-200">Taxas e Encargos</h3>

            <div className="grid grid-cols-12 gap-4 p-4 border rounded bg-gray-50 dark:bg-gray-700 dark:border-gray-600">
                <div className="col-span-4">
                    <SelectTextField
                        id="name"
                        label="Nome da Taxa"
                        value={newFee.value.name}
                        items={[
                            { value: 'Pré', label: 'Pré' },
                            { value: 'CDI', label: 'CDI' },
                            { value: 'CDI+', label: 'CDI+' },
                            { value: 'IPCA', label: 'IPCA' },
                            { value: 'IPCA+', label: 'IPCA+' },
                            { value: 'IGPM', label: 'IGPM' },
                            { value: 'IGPM+', label: 'IGPM+' }
                        ]}
                        onChange={(e) => {
                            newFee.value = { ...newFee.value, name: e.target.value };
                        }}
                    />
                </div>
                <div className="col-span-3">
                    <CurrencyInput
                        id="rate"
                        label="Taxa (%)"
                        prefix="%"
                        fractionDigits={2}
                        value={newFee.value.rate}
                        onChange={(value) => {
                            newFee.value = { ...newFee.value, rate: value };
                        }}
                    />
                </div>
                <div className="col-span-3">
                    <SelectTextField
                        id="interest_period"
                        label="Período"
                        value={newFee.value.interest_period}
                        items={[
                            { value: 'a.a', label: 'a.a' },
                            { value: 'a.m', label: 'a.m' },
                        ]}
                        onChange={(e) => {
                            newFee.value = { ...newFee.value, interest_period: e.target.value };
                        }}
                    />
                </div>
                <div className="col-span-2 flex items-end">
                    <button type="button"
                        onClick={handleAdd}
                        className="btn-add mb-4 disabled:opacity-50 disabled:cursor-not-allowed"
                        disabled={disabled}
                    >
                        Incluir
                    </button>
                </div>
            </div>

            <div className="mt-4 overflow-x-auto">
                <table className="min-w-full bg-white dark:bg-gray-800 border border-gray-300 dark:border-gray-600" aria-label="Taxas">
                    <thead className="bg-gray-50 dark:bg-gray-700">
                        <tr>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Nome</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Taxa</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Período</th>
                            <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-300 uppercase tracking-wider border-b dark:border-gray-600">Ações</th>
                        </tr>
                    </thead>
                    <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-600">
                        {fees.items.length === 0 ? (
                            <tr>
                                <td colSpan={4} className="px-4 py-8 text-center text-gray-500 dark:text-gray-400">
                                    Nenhuma taxa adicionada
                                </td>
                            </tr>
                        ) : (
                            fees.items.map((fee, index) => {
                                const isEditing = editingIndex.value === index;
                                return (
                                <tr key={`${index}-${fee.name}-${fee.rate}`} className="hover:bg-gray-50 dark:hover:bg-gray-700">
                                    <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100 font-medium">
                                        {isEditing ? (
                                            <SelectTextField
                                                id={`edit_fee_name_${index}`}
                                                label=""
                                                value={editingFee.value.name}
                                                items={[
                                                    { value: 'Pré', label: 'Pré' },
                                                    { value: 'Pós', label: 'Pós' },
                                                    { value: 'IOF', label: 'IOF' },
                                                    { value: 'TAC', label: 'TAC' },
                                                    { value: 'TEC', label: 'TEC' },
                                                    { value: 'Mora', label: 'Mora' },
                                                    { value: 'Multa', label: 'Multa' }
                                                ]}
                                                onChange={(e) => {
                                                    editingFee.value = { ...editingFee.value, name: e.target.value };
                                                }}
                                            />
                                        ) : (
                                            fee.name
                                        )}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100 font-medium">
                                        {isEditing ? (
                                            <CurrencyInput
                                                id={`edit_fee_rate_${index}`}
                                                label=""
                                                prefix=""
                                                fractionDigits={2}
                                                value={editingFee.value.rate}
                                                onChange={(value) => {
                                                    editingFee.value = { ...editingFee.value, rate: value };
                                                }}
                                            />
                                        ) : (
                                            `${fee.rate.toLocaleString('pt-BR', { minimumFractionDigits: 2 })}%`
                                        )}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap text-gray-900 dark:text-gray-100">
                                        {isEditing ? (
                                            <SelectTextField
                                                id={`edit_fee_period_${index}`}
                                                label=""
                                                value={editingFee.value.interest_period || ''}
                                                items={[
                                                    { value: '', label: 'Selecione' },
                                                    { value: 'MENSAL', label: 'MENSAL' },
                                                    { value: 'ANUAL', label: 'ANUAL' }
                                                ]}
                                                onChange={(e) => {
                                                    editingFee.value = { ...editingFee.value, interest_period: e.target.value };
                                                }}
                                            />
                                        ) : (
                                            fee.interest_period || '-'
                                        )}
                                    </td>
                                    <td className="px-4 py-2 whitespace-nowrap">
                                        {isEditing ? (
                                            <div className="flex gap-2">
                                                <button type="button"
                                                    onClick={() => handleSave(index)}
                                                    className="bg-green-500 text-white px-2 py-1 rounded text-xs hover:bg-green-600"
                                                >
                                                    Salvar
                                                </button>
                                                <button type="button"
                                                    onClick={handleCancel}
                                                    className="bg-gray-500 text-white px-2 py-1 rounded text-xs hover:bg-gray-600"
                                                >
                                                    Cancelar
                                                </button>
                                            </div>
                                        ) : (
                                            <div className="flex gap-2">
                                                <button type="button"
                                                    onClick={() => handleEdit(index)}
                                                    className="bg-blue-500 text-white px-2 py-1 rounded text-xs hover:bg-blue-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    disabled={disabled}
                                                >
                                                    Editar
                                                </button>
                                                <button type="button"
                                                    onClick={() => onRemove(index)}
                                                    className="bg-red-500 text-white px-2 py-1 rounded text-xs hover:bg-red-600 disabled:opacity-50 disabled:cursor-not-allowed"
                                                    disabled={disabled}
                                                >
                                                    Remover
                                                </button>
                                            </div>
                                        )}
                                    </td>
                                </tr>
                            )})
                        )}
                    </tbody>
                </table>
            </div>
        </div>
    );
};

export default FeesManager;
